import { Logger } from './logger';
import { getGlyphRenamePreflightErrors } from './rename-glyphs-preflight';
import { bindModalEscape } from './ui/modal-escape';
const console = new Logger('RenameGlyphsDialog');
function commonSubstring(names) {
    if (names.length === 0)
        return '';
    const [first, ...rest] = names;
    let best = '';
    for (let start = 0; start < first.length; start++) {
        for (let end = first.length; end > start + best.length; end--) {
            const candidate = first.slice(start, end);
            if (rest.every((name) => name.includes(candidate)))
                return candidate;
        }
    }
    return best;
}
/** Append `text` with each occurrence of `term` wrapped in a colored mark span. */
function appendMarkedText(parent, text, term, markClass) {
    if (!term) {
        parent.appendChild(document.createTextNode(text));
        return;
    }
    const parts = text.split(term);
    parts.forEach((part, index) => {
        if (part)
            parent.appendChild(document.createTextNode(part));
        if (index < parts.length - 1) {
            const mark = document.createElement('span');
            mark.className = markClass;
            mark.textContent = term;
            parent.appendChild(mark);
        }
    });
}
/**
 * Build the after-name display by mirroring search/replace, so only the
 * substituted segments are marked (not incidental occurrences of `replace`).
 */
function appendReplacedMarkedText(parent, name, search, replace) {
    if (!search) {
        parent.appendChild(document.createTextNode(name));
        return;
    }
    const parts = name.split(search);
    parts.forEach((part, index) => {
        if (part)
            parent.appendChild(document.createTextNode(part));
        if (index < parts.length - 1) {
            if (replace) {
                const mark = document.createElement('span');
                mark.className = 'rename-glyphs-replace-mark';
                mark.textContent = replace;
                parent.appendChild(mark);
            }
        }
    });
}
export class RenameGlyphsDialog {
    constructor() {
        this.modal = document.getElementById('rename-glyphs-modal');
        this.content = document.getElementById('rename-glyphs-modal-content');
        this.searchInput = null;
        this.replaceInput = null;
        this.preview = null;
        this.confirmButton = null;
        this.selectedNames = [];
        this.escapeBinding = null;
        this.buildContent();
        this.registerEvents();
    }
    open() {
        const glyphOverview = window.glyphOverviewInstance;
        this.selectedNames = glyphOverview?.getSelectedGlyphNames?.() || [];
        if (!this.modal || this.selectedNames.length === 0)
            return;
        this.searchInput.value = commonSubstring(this.selectedNames);
        this.replaceInput.value = '';
        this.updatePreview();
        this.modal.style.display = 'flex';
        this.escapeBinding?.release();
        this.escapeBinding = bindModalEscape(() => this.close(), {
            isOpen: () => this.modal?.style.display === 'flex'
        });
        requestAnimationFrame(() => this.searchInput?.focus());
    }
    close() {
        this.escapeBinding?.release();
        this.escapeBinding = null;
        if (this.modal)
            this.modal.style.display = 'none';
    }
    buildContent() {
        if (!this.content)
            return;
        const fields = document.createElement('div');
        fields.className = 'rename-glyphs-fields';
        const addField = (labelText) => {
            const label = document.createElement('label');
            label.textContent = labelText;
            const input = document.createElement('input');
            input.type = 'text';
            input.className = 'rename-glyphs-input';
            label.appendChild(input);
            fields.appendChild(label);
            return input;
        };
        this.searchInput = addField('Search');
        this.replaceInput = addField('Replace');
        const previewWrap = document.createElement('div');
        previewWrap.className = 'rename-glyphs-preview-wrap';
        const table = document.createElement('table');
        table.className = 'rename-glyphs-preview';
        table.innerHTML =
            '<colgroup><col><col></colgroup><thead><tr><th>Before</th><th>After</th></tr></thead>';
        this.preview = document.createElement('tbody');
        table.appendChild(this.preview);
        previewWrap.appendChild(table);
        const actions = document.createElement('div');
        actions.className = 'find-glyph-actions';
        const cancel = document.createElement('button');
        cancel.type = 'button';
        cancel.className = 'dialog-button';
        cancel.textContent = 'Cancel';
        cancel.addEventListener('click', () => this.close());
        this.confirmButton = document.createElement('button');
        this.confirmButton.type = 'button';
        this.confirmButton.className = 'dialog-button dialog-button-primary';
        this.confirmButton.disabled = true;
        this.confirmButton.addEventListener('click', () => this.rename());
        actions.append(cancel, this.confirmButton);
        this.content.replaceChildren(fields, previewWrap, actions);
    }
    registerEvents() {
        document
            .getElementById('rename-glyphs-modal-close-btn')
            ?.addEventListener('click', () => this.close());
        this.modal?.addEventListener('click', (event) => {
            if (event.target === this.modal)
                this.close();
        });
        this.searchInput?.addEventListener('input', () => this.updatePreview());
        this.replaceInput?.addEventListener('input', () => this.updatePreview());
    }
    getRenames() {
        const search = this.searchInput?.value || '';
        const replace = this.replaceInput?.value || '';
        return new Map(this.selectedNames
            .map((name) => [
            name,
            search ? name.split(search).join(replace) : name
        ])
            .filter(([before, after]) => before !== after));
    }
    /** Preflight conflicts keyed by current glyph name. */
    getPreflightErrors(renames) {
        const existingNames = window.currentFontModel?.glyphs.map((glyph) => glyph.name) || [];
        const errors = getGlyphRenamePreflightErrors(renames, existingNames);
        if (!this.searchInput?.value) {
            for (const name of this.selectedNames) {
                errors.set(name, 'Enter text to search for.');
            }
        }
        return errors;
    }
    canConfirm(renames, errors) {
        return renames.size > 0 && errors.size === 0;
    }
    updatePreview() {
        const renames = this.getRenames();
        const errors = this.getPreflightErrors(renames);
        const search = this.searchInput?.value || '';
        const replace = this.replaceInput?.value || '';
        this.preview?.replaceChildren(...this.selectedNames.map((name) => {
            const row = document.createElement('tr');
            const before = document.createElement('td');
            appendMarkedText(before, name, search, 'rename-glyphs-search-mark');
            const after = document.createElement('td');
            const glyphName = document.createElement('span');
            glyphName.className = 'rename-glyphs-result';
            if (renames.has(name)) {
                appendReplacedMarkedText(glyphName, name, search, replace);
            }
            else {
                glyphName.textContent = name;
            }
            const error = errors.get(name);
            if (error) {
                glyphName.classList.add('has-error');
                const comment = document.createElement('small');
                comment.className = 'rename-glyphs-comment';
                comment.textContent = error;
                after.append(glyphName, comment);
            }
            else {
                after.appendChild(glyphName);
            }
            row.append(before, after);
            return row;
        }));
        if (this.confirmButton) {
            this.confirmButton.disabled = !this.canConfirm(renames, errors);
            this.confirmButton.textContent = `Rename ${renames.size} Glyph${renames.size === 1 ? '' : 's'}`;
        }
    }
    rename() {
        const renames = this.getRenames();
        const errors = this.getPreflightErrors(renames);
        if (!this.canConfirm(renames, errors) || !window.currentFontModel)
            return;
        try {
            window.currentFontModel.renameGlyphs(renames);
            this.close();
        }
        catch (error) {
            console.error('Could not rename glyphs', error);
        }
    }
}
window.renameGlyphsDialog = new RenameGlyphsDialog();
